// Editar desde un lanzamiento un producto que YA está en Tiendanube
// (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento). Puro: sin red ni React.
//
//   · `fichaDesdeLaTienda`: la ficha armada con lo que hoy está publicado. Con eso arranca un producto
//     existente al sumarse a un lanzamiento (y "Traer lo de la tienda" la vuelve a poner), así lo que
//     después se marca como cambio es sólo lo que tocó la persona.
//   · `cambiosContraLaTienda`: qué campos de la ficha son distintos de lo publicado (para mostrarlos) y
//     el pedido de esos cambios para el backend (`cambios` del export).
//
// Lo que no se escribe en la tienda se dice, no se pierde en silencio: variantes que no existen allá
// (no se crean), propiedades renombradas, variantes cuando el producto de la tienda tiene varias y la
// ficha ninguna.

import { aNumero, normalizarTags, tnBodyDeProducto, type NivelDeCategoria } from "./fichaTiendanube";
import { fichaDe, type CategoriaDeTienda, type ProductoDeLanzamiento } from "./lanzamientosApi";

// ── Lo que devuelve el backend (`POST /v1/tiendanube/productos/estado`) ───────────────────────────

export interface VarianteEnLaTienda {
  id: number;
  valores: string[];
  precio: number | null;
  precio_promocional: number | null;
  costo: number | null;
  /** null = sin límite. */
  stock: number | null;
  sku: string | null;
  codigo_barras: string | null;
  peso: number | null;
  alto: number | null;
  ancho: number | null;
  profundidad: number | null;
}

export interface ProductoEnLaTienda {
  tn_product_id: number;
  nombre: string;
  descripcion: string | null;
  url: string | null;
  publicado: boolean;
  marca: string | null;
  tags: string | null;
  seo_titulo: string | null;
  seo_descripcion: string | null;
  atributos: string[];
  variantes: VarianteEnLaTienda[];
  categorias: number[];
  imagenes: Array<{ id: number; src: string }>;
}

// ── Comparar ──────────────────────────────────────────────────────────────────────────────────────

/** Sin espacios de más ni diferencias de mayúsculas en las claves de cruce. */
const clave = (valores: readonly string[]) => valores.map((v) => (v ?? "").trim().toLowerCase()).join("\u0000");

/** Un texto comparable: sin espacios de más (la descripción vuelve de Tiendanube con otros saltos). */
const plano = (v: string | null | undefined) => (v ?? "").replace(/\s+/g, " ").trim();

const mismoNumero = (a: string | number | null | undefined, b: number | null) => {
  const x = typeof a === "number" ? a : aNumero(a ?? null);
  if (x === null || b === null) return x === b;
  return Math.abs(x - b) < 0.005;
};

/** Cómo se muestra un valor de la tienda en la ficha ("—" si no tiene). */
const verNumero = (n: number | null) => (n === null ? "—" : String(n));
const verTexto = (t: string | null | undefined) => (plano(t) === "" ? "—" : plano(t));

/** El texto de un número para la ficha: sin ".00". */
const aTexto = (n: number | null) => (n === null ? null : String(n));

// ── Ficha desde la tienda ─────────────────────────────────────────────────────────────────────────

export interface FichaDesdeLaTienda {
  campos: {
    nombre: string;
    descripcion: string;
    precio: string;
    costo: string;
    stock: string;
    sku: string;
    codigoBarras: string;
    peso: string;
    alto: string;
    ancho: string;
    profundidad: string;
    marca: string;
    tags: string;
    seoTitulo: string;
    seoDescripcion: string;
    atributos: string[];
    precioPorVariante: boolean;
    envioPorVariante: boolean;
  };
  /** Vacío = producto de una sola variante (sus datos van en `campos`). */
  variantes: Array<{
    valores: string[];
    precio: string | null;
    costo: string | null;
    stock: string | null;
    sku: string | null;
    codigoBarras: string | null;
    peso: string | null;
    alto: string | null;
    ancho: string | null;
    profundidad: string | null;
  }>;
  /** Las categorías de Atlas que tienen par en la tienda. */
  categoriaIds: string[];
}

export function fichaDesdeLaTienda(e: ProductoEnLaTienda, arbol: readonly CategoriaDeTienda[]): FichaDesdeLaTienda {
  const conVariantes = e.atributos.length > 0 && e.variantes.length > 1;
  const primera = e.variantes[0] ?? null;
  const distintos = (f: (v: VarianteEnLaTienda) => unknown) => new Set(e.variantes.map((v) => String(f(v) ?? ""))).size > 1;
  const porId = new Map(arbol.filter((c) => c.tnCategoryId != null).map((c) => [c.tnCategoryId!, c.id]));
  return {
    campos: {
      nombre: e.nombre,
      descripcion: e.descripcion ?? "",
      precio: aTexto(primera?.precio ?? null) ?? "",
      costo: aTexto(primera?.costo ?? null) ?? "",
      // Con variantes, el stock y el SKU son de cada una: el producto no los repite.
      stock: conVariantes ? "" : (aTexto(primera?.stock ?? null) ?? ""),
      sku: conVariantes ? "" : (primera?.sku ?? ""),
      codigoBarras: conVariantes ? "" : (primera?.codigo_barras ?? ""),
      peso: aTexto(primera?.peso ?? null) ?? "",
      alto: aTexto(primera?.alto ?? null) ?? "",
      ancho: aTexto(primera?.ancho ?? null) ?? "",
      profundidad: aTexto(primera?.profundidad ?? null) ?? "",
      marca: e.marca ?? "",
      tags: e.tags ?? "",
      seoTitulo: e.seo_titulo ?? "",
      seoDescripcion: e.seo_descripcion ?? "",
      atributos: conVariantes ? e.atributos.slice(0, 3) : [],
      // Prendido sólo si de verdad cambian entre variantes: así la ficha se ve como la tienda.
      precioPorVariante: conVariantes && (distintos((v) => v.precio) || distintos((v) => v.costo)),
      envioPorVariante: conVariantes && ["peso", "alto", "ancho", "profundidad"].some((k) => distintos((v) => v[k as "peso"])),
    },
    variantes: conVariantes
      ? e.variantes.map((v) => ({
          valores: v.valores.slice(0, 3),
          precio: aTexto(v.precio),
          costo: aTexto(v.costo),
          stock: aTexto(v.stock),
          sku: v.sku,
          codigoBarras: v.codigo_barras,
          peso: aTexto(v.peso),
          alto: aTexto(v.alto),
          ancho: aTexto(v.ancho),
          profundidad: aTexto(v.profundidad),
        }))
      : [],
    categoriaIds: [...new Set(e.categorias.flatMap((id) => (porId.has(id) ? [porId.get(id)!] : [])))],
  };
}

// ── Qué cambia ────────────────────────────────────────────────────────────────────────────────────

/** La sección de la ficha donde está el campo (los `id` de `Seccion` en FichaDeProducto). */
export type SeccionDelCambio = "basico" | "precio" | "inventario" | "envio" | "variantes" | "categorias" | "seo";

export interface CambioDeCampo {
  /** El nombre del campo en la ficha (`nombre`, `precio`…), o `variante:<valores>`. */
  campo: string;
  etiqueta: string;
  seccion: SeccionDelCambio;
  /** Lo que hoy está publicado, para mostrarlo. */
  antes: string;
  /** Lo que queda en la tienda al publicar. */
  ahora: string;
  /**
   * El valor de la ficha que deja el campo como en la tienda ("Deshacer"). Sin él, el cambio no se
   * deshace desde el campo (categorías y variantes: se vuelve con "Traer lo de la tienda").
   */
  restaurar?: string;
}

/** El pedido para el backend: la forma de `cambios` en el export (`TnCambios` del backend). */
export interface CambiosParaLaTienda {
  nombre?: string;
  descripcion?: string;
  marca?: string;
  tags?: string;
  seo_titulo?: string;
  seo_descripcion?: string;
  categorias?: NivelDeCategoria[][];
  mantener_categorias?: number[];
  variantes?: Array<{
    tn_variant_id: number;
    precio?: string;
    costo?: string;
    stock?: string;
    sku?: string;
    codigo_barras?: string;
    peso?: number;
    alto?: number;
    ancho?: number;
    profundidad?: number;
  }>;
}

export type CambioDeVarianteParaLaTienda = NonNullable<CambiosParaLaTienda["variantes"]>[number];

export interface ComparacionConLaTienda {
  cambios: CambioDeCampo[];
  pedido: CambiosParaLaTienda;
  /** Lo que la ficha tiene distinto pero no se escribe en la tienda, explicado. */
  noSeEscriben: string[];
}

type CampoDeVariante = "precio" | "costo" | "stock" | "sku" | "codigo_barras" | "peso" | "alto" | "ancho" | "profundidad";

const ETIQUETA_DE_VARIANTE: Record<CampoDeVariante, { etiqueta: string; seccion: SeccionDelCambio }> = {
  precio: { etiqueta: "Precio", seccion: "precio" },
  costo: { etiqueta: "Costo", seccion: "precio" },
  stock: { etiqueta: "Stock", seccion: "inventario" },
  sku: { etiqueta: "SKU", seccion: "inventario" },
  codigo_barras: { etiqueta: "Código de barras", seccion: "inventario" },
  peso: { etiqueta: "Peso", seccion: "envio" },
  alto: { etiqueta: "Alto", seccion: "envio" },
  ancho: { etiqueta: "Ancho", seccion: "envio" },
  profundidad: { etiqueta: "Profundidad", seccion: "envio" },
};

/** Lo que la ficha manda para una variante, en el vocabulario del body de Tiendanube. */
interface DatosDeVariante {
  precio?: string;
  costo?: string;
  stock?: string;
  sku?: string;
  codigo_barras?: string;
  peso?: number;
  alto?: number;
  ancho?: number;
  profundidad?: number;
}

/** Compara los datos de UNA variante de la ficha con su par en la tienda. */
function compararVariante(
  datos: DatosDeVariante,
  enTienda: VarianteEnLaTienda,
  nombre: string | null,
  cambios: CambioDeCampo[],
  /** Con qué arrancó esta variante en la ficha (ver `tiendaBase`). null = se compara sólo contra la tienda. */
  deBase: VarianteEnLaTienda | null,
): CambioDeVarianteParaLaTienda | null {
  const pedido: Record<string, unknown> = { tn_variant_id: enTienda.id };
  const anotar = (campo: CampoDeVariante, antes: string, ahora: string) => {
    const { etiqueta, seccion } = ETIQUETA_DE_VARIANTE[campo];
    const enLaTienda = enTienda[campo];
    cambios.push({
      campo: nombre ? `variante:${nombre}:${campo}` : campo === "codigo_barras" ? "codigoBarras" : campo,
      etiqueta: nombre ? `${etiqueta} de ${nombre}` : etiqueta,
      seccion: nombre ? "variantes" : seccion,
      antes,
      ahora,
      ...(nombre ? {} : { restaurar: typeof enLaTienda === "number" ? String(enLaTienda) : (enLaTienda ?? "") }),
    });
  };
  // Precio y costo: un vacío en la ficha no borra (el backend no escribe un precio vacío).
  for (const campo of ["precio", "costo"] as const) {
    const ficha = datos[campo];
    if (ficha === undefined || aNumero(ficha) === null) continue;
    if (deBase && mismoNumero(ficha, deBase[campo])) continue;
    if (!mismoNumero(ficha, enTienda[campo])) {
      anotar(campo, verNumero(enTienda[campo]), ficha);
      pedido[campo] = ficha;
    }
  }
  // Stock: vacío en la ficha = "no lo toco" (no pasa a sin límite).
  // Una venta baja el stock en la tienda sola: si la persona no lo tocó, no se devuelve el de antes.
  const stockSinTocar = deBase !== null && datos.stock !== undefined && mismoNumero(datos.stock, deBase.stock);
  if (datos.stock !== undefined && aNumero(datos.stock) !== null && !stockSinTocar && !mismoNumero(datos.stock, enTienda.stock)) {
    anotar("stock", enTienda.stock === null ? "Sin límite" : String(enTienda.stock), datos.stock);
    pedido.stock = datos.stock;
  }
  for (const campo of ["sku", "codigo_barras"] as const) {
    // Sin precio por variante, la ficha no manda el código de barras de cada una (va el del producto, si
    // hay): que no venga es "no lo toco", no "borralo". Si no, un producto recién traído con precios
    // iguales le vaciaba los códigos a todas sus variantes (review Greptile del PR #198).
    if (campo === "codigo_barras" && datos.codigo_barras === undefined) continue;
    const ficha = plano(datos[campo]);
    if (deBase && ficha === plano(deBase[campo])) continue;
    if (ficha !== plano(enTienda[campo])) {
      anotar(campo, verTexto(enTienda[campo]), ficha === "" ? "—" : ficha);
      pedido[campo] = ficha;
    }
  }
  for (const campo of ["peso", "alto", "ancho", "profundidad"] as const) {
    const ficha = datos[campo];
    if (ficha === undefined) continue;
    if (deBase && mismoNumero(ficha, deBase[campo])) continue;
    if (!mismoNumero(ficha, enTienda[campo])) {
      anotar(campo, verNumero(enTienda[campo]), String(ficha));
      pedido[campo] = ficha;
    }
  }
  return Object.keys(pedido).length > 1 ? (pedido as unknown as CambioDeVarianteParaLaTienda) : null;
}

export function cambiosContraLaTienda(
  p: ProductoDeLanzamiento,
  e: ProductoEnLaTienda,
  arbol: readonly CategoriaDeTienda[],
): ComparacionConLaTienda {
  const body = tnBodyDeProducto(fichaDe(p, arbol));
  // Con qué arrancó la ficha (ADR-2026-10-23-publicar-manda-solo-lo-que-tocaste). Un campo es un cambio
  // sólo si la ficha es distinta de la base (lo tocó la persona) Y de la tienda de hoy: si la tienda
  // cambió sola (una venta), eso no se pisa. Sin base (filas de antes), cuenta todo lo distinto de la tienda.
  const base = p.tiendaBase ?? null;
  const tocado = (ficha: string | null | undefined, enBase: string | null | undefined) => !base || plano(ficha) !== plano(enBase);
  const cambios: CambioDeCampo[] = [];
  const pedido: CambiosParaLaTienda = {};
  const noSeEscriben: string[] = [];

  // ── Del producto ──
  if (plano(body.nombre) !== plano(e.nombre) && plano(p.nombre) !== "" && tocado(body.nombre, base?.nombre)) {
    cambios.push({ campo: "nombre", etiqueta: "Nombre", seccion: "basico", antes: e.nombre, ahora: body.nombre, restaurar: e.nombre });
    pedido.nombre = body.nombre;
  }
  if (plano(body.descripcion) !== plano(e.descripcion) && tocado(body.descripcion, base?.descripcion)) {
    cambios.push({
      campo: "descripcion",
      etiqueta: "Descripción",
      seccion: "basico",
      antes: verTexto(e.descripcion),
      ahora: verTexto(body.descripcion),
      restaurar: e.descripcion ?? "",
    });
    pedido.descripcion = body.descripcion;
  }
  const textos: Array<[keyof CambiosParaLaTienda & string, string, string | undefined, string | null, string, string | null | undefined]> = [
    ["marca", "Marca", body.marca, e.marca, "marca", base?.marca],
    ["tags", "Etiquetas", body.tags, normalizarTags(e.tags) ?? null, "tags", base ? (normalizarTags(base.tags) ?? null) : undefined],
    ["seo_titulo", "Título SEO", body.seo_titulo, e.seo_titulo, "seoTitulo", base?.seo_titulo],
    ["seo_descripcion", "Descripción SEO", body.seo_descripcion, e.seo_descripcion, "seoDescripcion", base?.seo_descripcion],
  ];
  for (const [clavePedido, etiqueta, ficha, tienda, campo, enBase] of textos) {
    if (plano(ficha) === plano(tienda) || !tocado(ficha, enBase)) continue;
    cambios.push({ campo, etiqueta, seccion: "seo", antes: verTexto(tienda), ahora: verTexto(ficha), restaurar: tienda ?? "" });
    (pedido as Record<string, unknown>)[clavePedido] = ficha ?? "";
  }

  // ── Categorías: el conjunto de la ficha contra el de la tienda ──
  // Las de la tienda que no tienen par en el árbol de la marca no se ven en la ficha: se mantienen.
  const tnDeAtlas = new Map(arbol.map((c) => [c.id, c.tnCategoryId]));
  const conocidas = new Set(arbol.flatMap((c) => (c.tnCategoryId != null ? [c.tnCategoryId] : [])));
  const deLaFicha = p.categorias.map((id) => tnDeAtlas.get(id) ?? null);
  const igualesA = (ids: readonly number[]) => {
    const conjunto = new Set(ids.filter((id) => conocidas.has(id)));
    return deLaFicha.every((id) => id !== null && conjunto.has(id)) && [...conjunto].every((id) => deLaFicha.includes(id));
  };
  const enTienda = new Set(e.categorias.filter((id) => conocidas.has(id)));
  const iguales = igualesA(e.categorias) || (base !== null && igualesA(base.categorias));
  if (!iguales) {
    const nombreDe = new Map(arbol.map((c) => [c.tnCategoryId, c.nombre]));
    cambios.push({
      campo: "categorias",
      etiqueta: "Categorías",
      seccion: "categorias",
      antes: [...enTienda].map((id) => nombreDe.get(id) ?? "?").join(", ") || "—",
      ahora: (body.categorias ?? []).map((camino) => camino[camino.length - 1]?.nombre ?? "").join(", ") || "—",
    });
    pedido.categorias = body.categorias ?? [];
    const mantener = e.categorias.filter((id) => !conocidas.has(id));
    if (mantener.length > 0) pedido.mantener_categorias = mantener;
  }

  // ── Variantes ──
  const variantesDelPedido: NonNullable<CambiosParaLaTienda["variantes"]> = [];
  const atributosFicha = body.atributos ?? [];
  if (body.variantes && body.variantes.length > 0) {
    if (clave(atributosFicha) !== clave(e.atributos)) {
      noSeEscriben.push(
        `Las propiedades (${atributosFicha.join(", ")}) no se cambian en la tienda: allá siguen ${e.atributos.join(", ") || "sin propiedades"}.`,
      );
    }
    const porValores = new Map(e.variantes.map((v) => [clave(v.valores), v]));
    const enBasePorValores = new Map((base?.variantes ?? []).map((v) => [clave(v.valores), v]));
    const nuevas: string[] = [];
    for (const v of body.variantes) {
      const nombre = v.valores.join(" · ");
      const par = porValores.get(clave(v.valores));
      if (!par) {
        nuevas.push(nombre);
        continue;
      }
      const r = compararVariante(v, par, nombre, cambios, enBasePorValores.get(clave(v.valores)) ?? null);
      if (r) variantesDelPedido.push(r);
    }
    if (nuevas.length > 0) {
      noSeEscriben.push(
        nuevas.length === 1
          ? `La variante ${nuevas[0]} no está en la tienda y no se agrega.`
          : `Las variantes ${nuevas.join(", ")} no están en la tienda y no se agregan.`,
      );
    }
  } else if (e.variantes.length === 1) {
    // Producto de una sola variante: sus datos son los del producto.
    const r = compararVariante(
      {
        precio: body.precio || undefined,
        costo: body.costo,
        stock: body.stock || undefined,
        sku: body.sku,
        codigo_barras: body.codigo_barras ?? "",
        peso: body.peso,
        alto: body.alto,
        ancho: body.ancho,
        profundidad: body.profundidad,
      },
      e.variantes[0]!,
      null,
      cambios,
      base?.variantes.length === 1 ? base.variantes[0]! : null,
    );
    if (r) variantesDelPedido.push(r);
  } else if (e.variantes.length > 1) {
    noSeEscriben.push(
      `En la tienda tiene ${e.variantes.length} variantes y la ficha no tiene ninguna: precio, stock y SKU no se cambian. Usá "Traer lo de la tienda" para verlas acá.`,
    );
  }
  if (variantesDelPedido.length > 0) pedido.variantes = variantesDelPedido;

  return { cambios, pedido, noSeEscriben };
}

/** ¿El pedido tiene algo para escribir? */
export function hayCambiosParaLaTienda(c: CambiosParaLaTienda): boolean {
  const { variantes, mantener_categorias: _soloConCategorias, ...producto } = c;
  return Object.values(producto).some((v) => v !== undefined) || (variantes ?? []).length > 0;
}
